import * as _ from 'lodash';
import AsyncButton from 'common/ui/AsyncButton';
import TypedSelect from 'common/ui/TypedSelect';
import useStartup from 'common/ui/useStartup';
import Merchant from 'domain/merchant';
import React, { useEffect, useState } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import SystemUser from './systemUser';
import merchantService from 'features/merchants/merchantService';
import systemUserService from './systemUserService';
import loggingService from 'common/logging/loggingService';
import Role from './role';
import roleService from './rolesService';
import { Trader } from 'features/settings/traders/trader';
import traderService from '../merchants/traders/traderService';
import ErrorList from 'common/ui/ErrorList';

export interface AddUserRoleAssignmentModalProps {
    onAdd(user: SystemUser, merchant: Merchant, role: Role): Promise<any>;
    onHide(): void;
    show: boolean;

    showUser?: boolean;
    showMerchant?: boolean;
    forMerchant?: Merchant;
    showRole?: boolean;

    errors: string[];
}

const AddUserRoleAssignmentModal: React.FC<AddUserRoleAssignmentModalProps> = props => {

    const [systemUsers, setSystemUsers] = useState<SystemUser[]>(null);
    const [merchants, setMerchants] = useState<Merchant[]>(null);
    const [traders, setTraders] = useState<Trader[]>(null);
    const [availableUsers, setAvailableUsers] = useState<(Trader | SystemUser)[]>(null);
    const [roles, setRoles] = useState<Role[]>(null);
    const [selectedUser, setSelectedUser] = useState<Trader | SystemUser>(null);
    const [selectedMerchant, setSelectedMerchant] = useState<Merchant>(null);
    const [selectedRole, setSelectedRole] = useState<Role>(null);
    const [isAddDisabled, setIsAddDisabled] = useState(true);
    
    useStartup(() => {
        if (props.showUser) {
            systemUserService.listSystemUsers()
                .then(setSystemUsers)
                .catch(err => {
                    loggingService.error("Unable to retrieve system users", err);
                });
        }

        if (props.showMerchant) {
            merchantService.listAllMerchants()
                .then(setMerchants)
                .catch(err => {
                    loggingService.error("Unable to retrieve merchants", err);
                });
        }

        if (props.showRole) {
            roleService.listRoles()
                .then(setRoles)
                .catch(err => {
                    loggingService.error("Unable to retrieve roles", err);
                })
        }
    });

    useEffect(() => {
        setSelectedMerchant(null);
        setSelectedUser(null);
        setSelectedRole(null);
    }, [props.show]);

    useEffect(() => {
        const merchantId = selectedMerchant?.id ?? props.forMerchant?.id;
        if (merchantId == null) return;

        traderService.listTraders(merchantId)
            .then(setTraders)
            .catch(err => {
                loggingService.error("Unable to retrieve traders", err)
            })
    }, [props.forMerchant, selectedMerchant]);

    useEffect(() => {
        const uniqueUsers = _.uniqBy( [
            ...systemUsers ?? [],
            ...traders ?? []
        ], m => m.userId);
        setAvailableUsers(_.orderBy(uniqueUsers, m => m.lastName));
    }, [systemUsers, traders])

    useEffect(() => {
        setIsAddDisabled(props.showUser && selectedUser == null ||
            props.showRole && selectedRole == null);
    }, [selectedUser, selectedRole])

    async function onAdd(): Promise<any> {
        await props.onAdd(selectedUser, selectedMerchant, selectedRole);
    }

    function isLoaded() {
        return (!props.showMerchant || merchants != null) &&
            (!props.showUser || systemUsers != null) &&
            (!props.showRole || roles != null);
    }

    if (!props.show || !isLoaded()) return <></>;

    return <>
        <Modal show onHide={props.onHide} backdrop="static">
            <Modal.Header closeButton>
                <Modal.Title>
                    Add User to Role
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>

                {props.showMerchant && 
                    <div className="col-8 pl-0">
                        <Form.Label>Merchant</Form.Label>
                        <TypedSelect
                            options={merchants}
                            showNullRow
                            nullRowText="--None--"
                            value={selectedMerchant}
                            textGetter={m => m.name}
                            valueGetter={m => m.id}
                            onValueSelected={setSelectedMerchant}
                        />
                    </div>
                }

                {props.showUser &&
                    <div className="col-8 pl-0">
                        <Form.Label>User</Form.Label>
                        <TypedSelect
                            options={availableUsers}
                            showNullRow
                            value={selectedUser}
                            textGetter={u => `${u.lastName}, ${u.firstName}`}
                            valueGetter={u => u.userId}
                            onValueSelected={setSelectedUser}
                        />
                    </div>
                }

                {props.showRole && 
                    <div className="col-8 pl-0">
                        <Form.Label>Role</Form.Label>
                        <TypedSelect
                            options={roles}
                            showNullRow
                            value={selectedRole}
                            textGetter={m => m.name}
                            valueGetter={m => m.id}
                            onValueSelected={setSelectedRole}
                        />
                    </div>
                }

                <ErrorList errors={props.errors} />
            </Modal.Body>
            <Modal.Footer>
                <div className="pull-right">
                    <Button variant="secondary" onClick={props.onHide}>
                        Cancel
                    </Button>
                    <AsyncButton variant="primary" className="ml-2" onClickAsync={onAdd} disabled={isAddDisabled}>
                        Add
                    </AsyncButton>
                </div>
            </Modal.Footer>
        </Modal>
    </>;
}

export default AddUserRoleAssignmentModal;